import { ResourceSchema } from '../data/types';

export const DSCVRY_PARTNER_SLUG = 'dscvry';
export const CONTACT_RESEARCH_SUBTYPE = 'Contact Research';
export const CONTACT_CONSENT_VERSION = '2026-10-05';
export const DSCVRY_LOGO_URL = 'https://app.dscvry.co/img/dscvry_logo-purple.3364b5b5.png';
export const DSCVRY_COVER_URLS = [
  'https://app.dscvry.co/img/login_page_sidepanel.eb1f680a.png',
  'https://cdn.prod.website-files.com/638145ad5b76d51aa0131ef2/65a5a9fba7fdee6cac0b304e_jen-theodore-Tys0QOcdbBA-unsplash%20(1).jpg',
  'https://cdn.prod.website-files.com/638145ad5b76d51aa0131ef2/65a6ef460ce39f8e02914497_mindspace-studio-iF8GB3WCEls-unsplashpurple.jpg',
];
export const DSCVRY_HUB_URL = 'https://airtable.com/appA4hnizMi7SAm0y/shrN6pi33esu49Htc/tblK0oIS3jURkqrzX/viwyMWkbAnx62aPt1';
export const DSCVRY_REGISTER_URL = 'https://app.dscvry.co/#/register-user?type=founder';

export const NOT_SHARED = [
  'financial statements',
  'bank transactions',
  'POS data',
  'menu or margin data',
  'inventory information',
  'diner information',
];

type ResourceWithHandoff = ResourceSchema & {
  name?: string
  logo?: string
  partner_handoff?: {
    partner?: { slug?: string; signup_url?: string; hub_url?: string; capabilities?: string[] }
  }
};

/** Expert listings configured for contact research, including the DSCVRY partner slug. */
export const isContactBuilderResource = (resource?: ResourceWithHandoff | null): boolean => {
  if (!resource || resource.resource_type !== 'expert') return false;
  const subtype = (resource.resource_subtype || '').trim().toLowerCase();
  const partner = resource.partner_handoff?.partner;
  const capabilities = partner?.capabilities || [];
  return subtype === CONTACT_RESEARCH_SUBTYPE.toLowerCase()
    || capabilities.includes('contact_research')
    || (partner?.slug || '').toLowerCase() === DSCVRY_PARTNER_SLUG;
};

/** DSCVRY logo only for the DSCVRY contact-research expert. Other listings keep their own logo. */
export const isDscvryResource = (resource?: ResourceWithHandoff | null): boolean => {
  if (!isContactBuilderResource(resource)) return false;
  const slug = (resource?.partner_handoff?.partner?.slug || '').toLowerCase();
  const name = (resource?.name || '').toLowerCase();
  return slug === DSCVRY_PARTNER_SLUG || name.includes('dscvry');
};

export const resourceLogoUrl = (resource?: ResourceWithHandoff | null): string => {
  if (isDscvryResource(resource)) return DSCVRY_LOGO_URL;
  return resource?.logo || '';
};

/** Founder register URL, or the listing hub URL when one is stored. */
export const dscvryOpenUrl = (resource?: ResourceWithHandoff | null): string => {
  const hub = (resource?.partner_handoff?.partner?.hub_url || '').trim();
  return hub || DSCVRY_REGISTER_URL;
};

const appendProjectRef = (url: string, projectId: string) => {
  if (!url || !projectId || /(?:\?|&)ref=/.test(url)) return url;
  const hashAt = url.indexOf('#');
  if (hashAt >= 0) {
    const hash = url.slice(hashAt + 1);
    return `${url.slice(0, hashAt)}#${hash}${hash.includes('?') ? '&' : '?'}ref=${encodeURIComponent(projectId)}`;
  }
  return `${url}${url.includes('?') ? '&' : '?'}ref=${encodeURIComponent(projectId)}`;
};

/** Tab opened by Hand list to DSCVRY: the app host when the API returns one, otherwise the founder platform URL with the project id. */
export const platformHandoffUrl = (resource: ResourceWithHandoff | null | undefined, returnedOpenUrl: string, projectId: string) => {
  let host = '';
  try { host = new URL(returnedOpenUrl || '').host; } catch { host = ''; }
  if (host === 'app.dscvry.co') return returnedOpenUrl;
  return appendProjectRef(dscvryOpenUrl(resource), projectId);
};

/** DSCVRY heroes use cover photos. The logo stays in the header. Stored kitchen photos stay on the record. */
export const dscvryBrandPhotos = (resource?: ResourceWithHandoff | null, stored: string[] = []): string[] => {
  if (isDscvryResource(resource)) return DSCVRY_COVER_URLS;
  return stored;
};

export const isDscvryBrandPhoto = (url?: string): boolean => (url || '') === DSCVRY_LOGO_URL;

export const isDscvryCoverPhoto = (url?: string): boolean => DSCVRY_COVER_URLS.includes(url || '');

export type BusinessBriefInput = {
  id?: string
  business_name?: string
  business_category?: string
  business_type?: string
  description?: string
  cooking_city?: string
  cooking_state?: string
};

/** Map a selected pantry business into the brief. Financial and diner fields stay out. */
export const briefFromBusiness = (business: BusinessBriefInput) => {
  const name = (business.business_name || '').trim();
  const category = (business.business_category || '').trim();
  const kind = (business.business_type || '').trim();
  const description = (business.description || '').trim();
  const concept = [description, category, kind].filter(Boolean).join('. ');
  const city = (business.cooking_city || '').trim();
  const state = (business.cooking_state || '').trim();
  const places = [city, state].filter(Boolean);
  const label = name || 'Primary contacts';
  return {
    business_id: business.id || '',
    business_description: concept || name,
    answers: {
      business_name: name,
      business_description: concept || name,
      geographies: places,
      lists: [{
        id: 'primary',
        label,
        target: 25,
        qualifying_signals: [category, ...places].filter(Boolean),
      }],
      qualifying_signals: [name, category, kind].filter(Boolean),
      research_context: {
        venture_name: name,
        concept: concept || name,
        business_id: business.id || '',
        category,
        city,
        state,
        setup_location: 'dscvry_business_hub',
        target_customers: [],
      },
      connector: { id: 'dscvry', target_customers: { primary: label } },
    },
  };
};

/** Expert detail path for a selected business, falling back to the expert list. */
export const dscvryExpertPath = (
  resources: Array<{ id?: string } & ResourceWithHandoff>,
  businessId: string,
): string => {
  const match = resources.find((item) => isDscvryResource(item))
    || resources.find((item) => isContactBuilderResource(item));
  const query = businessId ? `?business=${encodeURIComponent(businessId)}` : '';
  if (match?.id) return `/expert/${match.id}${query}`;
  return `/expert${query}`;
};

export const buildHandoffUrl = (signupUrl: string | undefined, projectId: string): string => {
  if (!signupUrl || !projectId) return '';
  try {
    const url = new URL(signupUrl);
    url.searchParams.set('ref', projectId);
    url.hash = '';
    return url.toString();
  } catch {
    return '';
  }
};
